import { Text } from "@/components/ui/baseUi/text"
import { cn } from "@/modules/lib/utils"
import { WarningIcon } from "@images/svgIcons/warningIcon"
import { BoxConsumableDetailsEntity } from "@modules/boxConsumable/entities/boxConsumableDetailsEntity"
import { BoxOfferFormattedPrice } from "@modules/boxOffer/components/boxOfferFormSection"
import { BoxOfferType } from "@modules/boxOffer/entities/boxOfferTypes"
import {
  getBoxOfferIcon,
  getOfferCommitment,
  getSubscriptionCommitmentPeriod,
} from "@modules/boxOffer/utils/boxOfferUtils"
import { BoxSubscriptionDetailsEntity } from "@modules/boxSubscription/entities/boxSubscriptionDetailsEntity"
import { BoxSubscriptionStatus } from "@modules/boxSubscription/gateway/boxSubscriptionApiDto"
import { word } from "@modules/core/utils/i18n"
import { useLanguage } from "@modules/language/hooks/useLanguage"
import { capitalize } from "@modules/utils/stringUtils"
import dayjs from "dayjs"
import { motion, useInView } from "framer-motion"
import { ChevronDown, ChevronUp, Dot, Percent, Tag } from "lucide-react"
import { useRef, useState } from "react"

type Props = {
  item: BoxConsumableDetailsEntity | BoxSubscriptionDetailsEntity
  menuActions?: React.JSX.Element
  className?: string
} & React.ComponentProps<"div">

export function MemberOfferItem({ item, menuActions, className, ...props }: Props) {
  const ref = useRef<HTMLDivElement>(null)
  const language = useLanguage()
  const isInView = useInView(ref, { initial: false })

  const [isCollapsed, setIsCollapsed] = useState(true)

  const toggleCollapse = () => {
    setIsCollapsed((prev) => !prev)
  }

  const currentPrice = item.getCurrentPrice()

  return (
    <div
      className={cn("mb-0 flex w-full flex-col gap-6 rounded-2xl bg-neutral-100 px-6 py-4", className)}
      key={item.id}
      {...props}
    >
      {/* HEADER */}
      <div className="flex w-full flex-col gap-2">
        <div className="flex w-full flex-row items-center justify-between">
          <div className="flex flex-row items-center">
            <Text
              fontStyle="BM"
              className="w-40 max-w-40 wrap-break-word whitespace-break-spaces text-textDefault-body"
            >
              {item.offer.name}
            </Text>
            <Dot strokeWidth={10} color="currentColor" className={item.getColorStatus()} />
            <Text fontStyle="LS" className="max-w-20 wrap-break-word whitespace-break-spaces text-textDisabled">
              {item.getWordingStatus()}
            </Text>
          </div>
          <div className="flex gap-2">
            {item instanceof BoxConsumableDetailsEntity && item.offer.type === BoxOfferType.TicketsBook && (
              <div className="flex flex-row items-center gap-2 rounded-full border border-borderDefault px-2 py-1">
                <Text fontStyle="LL" className="text-textDefault-body">
                  {item.count}/{item.offer.count}
                </Text>
                <Text fontStyle="BS" className="text-textDefault-caption">
                  {word("box.member.table.offer.item.sessionsLeft")}
                </Text>
              </div>
            )}
            {isCollapsed ? (
              <ChevronDown size={25} onClick={toggleCollapse} className="cursor-pointer" />
            ) : (
              <ChevronUp size={25} onClick={toggleCollapse} className="cursor-pointer" />
            )}
          </div>
        </div>
        {/* WARNING */}
        {item.isDisplayWarning() && (
          <div className="flex w-max items-center gap-1 rounded-full bg-surface-error px-2 py-1 text-center">
            <WarningIcon color="currentColor" width={14} height={14} className="text-textError" />
            <Text fontStyle="LS" className="text-textDefault-body">
              {item.getWordingWarning()}
            </Text>
          </div>
        )}
      </div>
      {/* BODY EXPANDABLE */}
      <motion.div
        animate={{ height: isCollapsed ? 0 : "auto" }}
        className={cn(
          "w-full flex-col gap-4",
          isCollapsed ? "hidden" : "flex",
          !isCollapsed && isInView ? "overflow-visible" : "overflow-hidden",
        )}
      >
        {/* TYPE */}
        <div className="flex w-full flex-col gap-2">
          <div className="flex w-full items-baseline gap-2">
            <Text fontStyle="BS" className="text-textDefault-caption">
              {word("box.member.table.offer.item.type")}
            </Text>
            <div className="flex grow border-t border-borderDefault" />
          </div>
          <div className="flex w-full items-center gap-2">
            {getBoxOfferIcon(item.offer.type)}
            <Text fontStyle="LL" className="text-textDefault-body">
              {word(`box.offer.form.types.${item.offer.type}`)}
            </Text>
          </div>
        </div>
        {/* STATUS */}
        <div className="flex w-full flex-col gap-2">
          <div className="flex w-full items-baseline gap-2">
            <Text fontStyle="BS" className="text-textDefault-caption">
              {word("box.member.table.offer.item.status.label")}
            </Text>
            <div className="flex grow border-t border-borderDefault" />
          </div>
          <div className="flex w-full flex-col gap-1">
            <div className="flex items-center gap-1 pl-4">
              <Text fontStyle="LL" className="text-textDefault-body">
                {item.getWordingStatus()}
              </Text>
              {item.status === BoxSubscriptionStatus.PAUSE && item.pause && (
                <>
                  <Text fontStyle="LM" className="text-textDefault-body">
                    ·
                  </Text>
                  <Text fontStyle="BS" className="text-textDefault-caption">
                    {item.getWordingPause()}
                  </Text>
                </>
              )}
            </div>
            <div className="flex items-center gap-1 pl-4">
              <Text fontStyle="LM" className="text-textDefault-body">
                {word("box.member.table.offer.item.start")}
              </Text>
              <Text fontStyle="LM" className="text-textDefault-body">
                ·
              </Text>
              <Text fontStyle="BS" className="text-textDefault-caption">
                {item.deferredDate ? dayjs(item.deferredDate).format("L") : dayjs(item.creationDate).format("L")}
              </Text>
            </div>

            {item instanceof BoxSubscriptionDetailsEntity && item.subscriptionEndDate && (
              <div className="flex items-center gap-1 pl-4">
                <Text fontStyle="LM" className="text-textDefault-body">
                  {word("box.member.table.offer.item.end")}
                </Text>
                <Text fontStyle="LM" className="text-textDefault-body">
                  ·
                </Text>
                <Text fontStyle="BS" className="text-textDefault-caption">
                  {dayjs(item.subscriptionEndDate).format("L")}
                </Text>
              </div>
            )}
            {item instanceof BoxConsumableDetailsEntity && item.endValidity && (
              <div className="flex items-center gap-1 pl-4">
                <Text fontStyle="LM" className="text-textDefault-body">
                  {word("box.member.table.offer.item.end")}
                </Text>
                <Text fontStyle="LM" className="text-textDefault-body">
                  ·
                </Text>
                <Text fontStyle="BS" className="text-textDefault-caption">
                  {dayjs(item.endValidity).format("L")}
                </Text>
              </div>
            )}
            {item.status !== BoxSubscriptionStatus.INACTIVE && (
              <div className="flex items-start gap-1 pl-4">
                <Text fontStyle="LM" className="text-textDefault-body">
                  {item.offer.type === BoxOfferType.Subscription || item.offer.type === BoxOfferType.Millenium
                    ? word("box.member.table.offer.item.commitmentPeriod")
                    : word("box.member.table.offer.item.validityPeriod")}
                </Text>
                <Text fontStyle="LM" className="text-textDefault-body">
                  ·
                </Text>
                <div className="flex flex-col items-start gap-1">
                  <Text fontStyle="LM" className="text-textDefault-caption">
                    {capitalize(
                      item instanceof BoxSubscriptionDetailsEntity
                        ? getSubscriptionCommitmentPeriod(item)
                        : getOfferCommitment(item.offer),
                    )}
                  </Text>
                  {item.offer.commitmentPeriodAutoRenewal && (
                    <Text fontStyle="LM" className="text-textDefault-caption">
                      {word("box.offer.form.infos.commitmentPeriodAutoRenewal.active")}
                    </Text>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
        {/* PRICE */}
        <div className="flex w-full flex-col gap-2">
          <div className="flex w-full items-baseline gap-2">
            <Text fontStyle="BS" className="font-semibold text-textDefault-caption">
              {word("box.member.table.offer.item.price")}
            </Text>
            <div className="flex grow border-t border-borderDefault" />
          </div>
          <div className="flex w-full items-center gap-1">
            {item.status === BoxSubscriptionStatus.SCHEDULED_ACTIVATION && item.referencePrice ? (
              <BoxOfferFormattedPrice
                price={item.referencePrice}
                showRecurringPeriod={item instanceof BoxSubscriptionDetailsEntity}
                recurringPeriod={item instanceof BoxSubscriptionDetailsEntity ? item.recurringPeriod : undefined}
                wholePartStyle="LL"
                decimalPartStyle="LL"
                periodStyle="LL"
              />
            ) : currentPrice ? (
              <>
                <BoxOfferFormattedPrice
                  price={currentPrice}
                  showRecurringPeriod={item instanceof BoxSubscriptionDetailsEntity}
                  recurringPeriod={item instanceof BoxSubscriptionDetailsEntity ? item.recurringPeriod : undefined}
                  wholePartStyle="LL"
                  decimalPartStyle="LL"
                  periodStyle="LL"
                />
                {item.referencePrice && item.referencePrice.value !== currentPrice.value ? (
                  <BoxOfferFormattedPrice
                    price={item.referencePrice}
                    showRecurringPeriod={item instanceof BoxSubscriptionDetailsEntity}
                    recurringPeriod={item instanceof BoxSubscriptionDetailsEntity ? item.recurringPeriod : undefined}
                    wholePartStyle="LM"
                    decimalPartStyle="LM"
                    periodStyle="LM"
                    lineThrough={true}
                  />
                ) : null}
              </>
            ) : (
              <Text fontStyle="LL" className="text-textDefault-body">
                {word("box.member.table.offer.item.freePrice")}
              </Text>
            )}
          </div>

          <div className="flex w-full flex-col gap-4">
            {/* PROMO CODE */}
            {item.usedPromoCode && item.referencePrice ? (
              <div className="flex w-full flex-col gap-2">
                <div className="flex items-center gap-1 pl-4">
                  <Percent size={15} color="currentColor" className="text-textDisabled" />
                  <Text fontStyle="LS" className="text-textDisabled">
                    {word("box.member.table.offer.item.codePromo")}
                  </Text>
                </div>
                <div className="flex flex-col gap-1 pl-4">
                  <Text fontStyle="LM" className="text-textDefault-body">
                    {item.getAppliedPromocodeWording(language)}
                  </Text>
                </div>
              </div>
            ) : null}
            {/* DISCOUNT */}
            {item.coupon && item.priceObject ? (
              <div className="flex w-full flex-col gap-2">
                <div className="flex items-center gap-1 pl-4">
                  <Tag size={15} color="currentColor" className="text-textDisabled" />
                  <Text fontStyle="LS" className="font-semibold text-textDisabled">
                    {word("box.member.table.offer.item.discount.label")}
                  </Text>
                </div>
                <div className="flex flex-col gap-1 pl-4">
                  <Text fontStyle="BS" className="text-textDefault-body">
                    {item.getAppliedCouponWording(language)}
                  </Text>
                  <Text fontStyle="LS" className="text-textDefault-caption">
                    {item.getWordingCouponDate()}
                  </Text>
                </div>
              </div>
            ) : null}
          </div>
        </div>
        {menuActions ? (
          <div className="flex w-full flex-row-reverse" ref={ref}>
            {menuActions}
          </div>
        ) : null}
      </motion.div>
    </div>
  )
}
